// src/pages/monitoring/progress/ProgressList.jsx
import React, { useState, useEffect } from 'react';
import { Eye, ChevronDown, ChevronRight, Package, TrendingUp, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import ExpandableDataTable from '../../../components/common/ExpandableDataTable';
import projectProgressService, {
  getRAGStatusBadgeClass,
  getProgressStatusBadgeClass,
  getRAGStatusIcon,
  formatProgress,
  getProgressBarColor
} from '../../../services/projectProgressService';
import { useNavigate } from 'react-router-dom';

/**
 * Progress Monitoring List Component
 * 
 * Features:
 * - Expandable table showing all projects with progress
 * - RAG status filters (RED, AMBER, GREEN)
 * - Type filters (TOWER, FO)
 * - Search by project name/code/customer
 * - Expand row to show segments/sites with progress
 * - Click "View Details" to navigate to ProjectProgressDetail
 * 
 * Data Flow:
 * 1. Fetch all projects with progress summary
 * 2. On expand: Fetch project details with segments/sites
 * 3. Display segments/sites with progress bars and RAG status
 */
const ProgressList = () => {
  const navigate = useNavigate();
  
  // State
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [projectDetails, setProjectDetails] = useState({});
  const [loadingDetails, setLoadingDetails] = useState(new Set());
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('ALL');
  const [filterRAG, setFilterRAG] = useState('ALL');
  
  // Pagination (client-side for now)
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortConfig, setSortConfig] = useState({ sortBy: 'projectCode', direction: 'ASC' });

  // Fetch projects on mount
  useEffect(() => {
    fetchProjects();
  }, []);

  // Fetch all projects with progress summary
  const fetchProjects = async () => {
    setIsLoading(true);
    try {
      const data = await projectProgressService.getAllProjectsProgress();
      setProjects(data);
      console.log('✅ Loaded projects:', data.length);
    } catch (error) {
      console.error('❌ Error fetching projects:', error);
      toast.error('Failed to load projects');
    } finally {
      setIsLoading(false);
    }
  };

  // Toggle expand row and fetch details if needed
  const toggleExpandRow = async (projectId) => {
    const isCurrentlyExpanded = expandedRows.has(projectId);
    
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (isCurrentlyExpanded) {
        newSet.delete(projectId);
      } else {
        newSet.add(projectId);
      }
      return newSet;
    });

    // Fetch project details if expanding and not already loaded
    if (!isCurrentlyExpanded && !projectDetails[projectId]) {
      setLoadingDetails(prev => new Set(prev).add(projectId));
      
      try {
        const projectDetail = await projectProgressService.getProjectProgressById(projectId);
        
        setProjectDetails(prev => ({
          ...prev,
          [projectId]: projectDetail
        }));
        
        console.log('✅ Loaded details for project:', projectId);
      } catch (error) {
        console.error('❌ Error fetching project details:', error);
        toast.error('Failed to load project details');
        
        // Collapse row on error
        setExpandedRows(prev => {
          const newSet = new Set(prev);
          newSet.delete(projectId);
          return newSet;
        });
      } finally {
        setLoadingDetails(prev => {
          const newSet = new Set(prev);
          newSet.delete(projectId);
          return newSet;
        });
      }
    }
  };

  // Navigate to detail view
  const handleViewDetails = (project) => {
    navigate(`/monitoring/progress/${project.id}`);
  };

  // Filter projects
  const filteredProjects = projects.filter(project => {
    // Search filter
    const matchesSearch = 
      !searchTerm ||
      project.projectName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.projectCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.customerName?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Type filter
    const matchesType = filterType === 'ALL' || project.projectType === filterType;
    
    // RAG filter
    const matchesRAG = filterRAG === 'ALL' || project.overallRAGStatus === filterRAG;
    
    return matchesSearch && matchesType && matchesRAG;
  });

  // Sort projects
  const sortedProjects = [...filteredProjects].sort((a, b) => {
    let aValue = a[sortConfig.sortBy];
    let bValue = b[sortConfig.sortBy];
    
    // Handle null values
    if (aValue === null || aValue === undefined) return 1;
    if (bValue === null || bValue === undefined) return -1;
    
    if (aValue < bValue) return sortConfig.direction === 'ASC' ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === 'ASC' ? 1 : -1;
    return 0;
  });

  // Paginate
  const startIndex = currentPage * pageSize;
  const paginatedProjects = sortedProjects.slice(startIndex, startIndex + pageSize);

  // Define columns
  const columns = [
    {
      key: 'no',
      label: 'No',
      sortable: false,
      width: 'w-16',
      render: (row, index) => (
        <span className="font-medium text-gray-900">
          {startIndex + index + 1}
        </span>
      )
    },
    {
      key: 'projectCode',
      label: 'Project Code',
      sortable: true,
      render: (row) => (
        <div>
          <span className="font-bold text-blue-600">{row.projectCode}</span>
          <div className="text-xs text-gray-500 mt-1">ID: {row.id}</div>
        </div>
      )
    },
    {
      key: 'projectName',
      label: 'Project Name',
      sortable: true,
      nowrap: false,
      render: (row) => (
        <div>
          <div className="font-semibold text-gray-900">{row.projectName}</div>
          <div className="text-xs text-gray-600 mt-1">{row.customerName}</div>
        </div>
      )
    },
    {
      key: 'projectType',
      label: 'Type',
      sortable: true,
      render: (row) => (
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
          row.projectType === 'TOWER' 
            ? 'bg-purple-100 text-purple-800' 
            : 'bg-green-100 text-green-800'
        }`}>
          {row.projectType}
        </span>
      )
    },
    {
      key: 'items',
      label: 'Items',
      sortable: false,
      render: (row) => {
        const itemCount = row.projectType === 'TOWER' 
          ? (row.towerSites?.length || 0)
          : (row.foSegments?.length || 0);
        const itemLabel = row.projectType === 'TOWER' ? 'Sites' : 'Segments';
        
        return (
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleExpandRow(row.id);
            }}
            className="flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-2 py-1 rounded-lg transition-colors"
          >
            {expandedRows.has(row.id) ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
            <span className="font-semibold">
              {itemCount} {itemLabel}
            </span>
          </button>
        );
      }
    },
    {
      key: 'totalProgress',
      label: 'Progress',
      sortable: true,
      render: (row) => (
        <div className="flex items-center gap-3">
          <div className="flex-1 bg-gray-200 rounded-full h-2 w-24">
            <div 
              className={`h-2 rounded-full transition-all ${getProgressBarColor(row.progressVariance)}`}
              style={{ width: `${Math.min(row.totalProgress || 0, 100)}%` }}
            />
          </div>
          <span className="text-sm font-bold text-gray-900 min-w-[45px]">
            {formatProgress(row.totalProgress)}
          </span>
        </div>
      )
    },
    {
      key: 'progressStatus',
      label: 'Status',
      sortable: true,
      render: (row) => (
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${getProgressStatusBadgeClass(row.progressStatus)}`}>
          {row.progressStatus?.replace('_', ' ')}
        </span>
      )
    },
    {
      key: 'overallRAGStatus',
      label: 'RAG',
      sortable: true,
      render: (row) => (
        <div className="flex items-center justify-center">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center shadow-md ${getRAGStatusBadgeClass(row.overallRAGStatus)}`}>
            <span className="text-xl">{getRAGStatusIcon(row.overallRAGStatus)}</span>
          </div>
        </div>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      render: (row) => (
        <button
          onClick={() => handleViewDetails(row)}
          className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
          title="View Details"
        >
          <Eye className="w-5 h-5" />
        </button>
      )
    }
  ];

  // Render expanded content (segments/sites with progress)
  const renderExpandedContent = (project) => {
    const isLoadingDetail = loadingDetails.has(project.id);
    const projectDetail = projectDetails[project.id];
    
    const itemLabel = project.projectType === 'TOWER' ? 'Tower Sites' : 'FO Segments';
    const items = projectDetail 
      ? (project.projectType === 'TOWER' ? projectDetail.towerSites : projectDetail.foSegments)
      : [];
    
    return (
      <div className="px-6 py-4 bg-gradient-to-r from-blue-50 to-gray-50">
        <div className="ml-8">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-bold text-gray-800 flex items-center gap-2">
              <Package className="w-4 h-4 text-red-600" />
              {itemLabel} ({items.length})
            </h4>
            <button
              onClick={() => handleViewDetails(project)}
              className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
            >
              <Eye className="w-3 h-3" />
              View Full Details
            </button>
          </div>
          
          {isLoadingDetail ? (
            // Loading state
            <div className="bg-white rounded-lg border border-blue-200 p-8">
              <div className="flex flex-col items-center justify-center space-y-3">
                <div className="flex space-x-2">
                  <div className="w-3 h-3 bg-blue-400 rounded-full animate-bounce"></div>
                  <div className="w-3 h-3 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                  <div className="w-3 h-3 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                </div>
                <p className="text-gray-500 text-sm">Loading {itemLabel.toLowerCase()}...</p>
              </div>
            </div>
          ) : items.length > 0 ? (
            <div className="space-y-2">
              {items.map(item => (
                <div 
                  key={item.id} 
                  className="bg-white p-4 rounded-lg border border-gray-200 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        project.projectType === 'TOWER' 
                          ? 'bg-purple-100' 
                          : 'bg-green-100'
                      }`}>
                        <Package className={`w-4 h-4 ${
                          project.projectType === 'TOWER' 
                            ? 'text-purple-600' 
                            : 'text-green-600'
                        }`} />
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold text-sm text-gray-900">
                          {project.projectType === 'TOWER' ? item.siteId : item.spanId}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                          {project.projectType === 'TOWER' 
                            ? item.siteName 
                            : item.siteNameAB}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      {/* Progress Bar */}
                      <div className="flex items-center gap-2">
                        <div className="bg-gray-200 rounded-full h-2 w-24">
                          <div 
                            className={`h-2 rounded-full transition-all ${
                              project.projectType === 'TOWER' 
                                ? 'bg-purple-600' 
                                : 'bg-green-600'
                            }`}
                            style={{ width: `${Math.min(item.progressPercentage || 0, 100)}%` }}
                          />
                        </div>
                        <span className="text-xs font-bold text-gray-900 min-w-[45px]">
                          {formatProgress(item.progressPercentage)}
                        </span>
                      </div>
                      
                      {/* RAG Status */}
                      <div className={`w-6 h-6 rounded-full ${getRAGStatusBadgeClass(item.ragStatus)}`}></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            // Empty state
            <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
              <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">No {itemLabel.toLowerCase()} data available</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render main component
  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-red-600 to-red-700 rounded-lg flex items-center justify-center shadow-md">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Project Progress Monitoring</h1>
              <p className="text-gray-600 mt-1">Track and monitor construction project progress in real-time</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <AlertCircle className="w-6 h-6 text-red-600" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-4 border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="md:col-span-1">
            <div className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search projects..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
              <svg className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
          
          {/* Type Filter */}
          <div className="md:col-span-1">
            <div className="flex gap-2">
              <button
                onClick={() => setFilterType('ALL')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filterType === 'ALL'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All Types
              </button>
              <button
                onClick={() => setFilterType('TOWER')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filterType === 'TOWER'
                    ? 'bg-purple-600 text-white'
                    : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                }`}
              >
                TOWER
              </button>
              <button
                onClick={() => setFilterType('FO')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filterType === 'FO'
                    ? 'bg-green-600 text-white'
                    : 'bg-green-100 text-green-700 hover:bg-green-200'
                }`}
              >
                FO
              </button>
            </div>
          </div>
          
          {/* RAG Filter */}
          <div className="md:col-span-1">
            <div className="flex gap-2">
              <button
                onClick={() => setFilterRAG('ALL')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filterRAG === 'ALL'
                    ? 'bg-gray-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All RAG
              </button>
              <button
                onClick={() => setFilterRAG('GREEN')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filterRAG === 'GREEN'
                    ? 'bg-green-600 text-white'
                    : 'bg-green-100 text-green-700 hover:bg-green-200'
                }`}
              >
                🟢 GREEN
              </button>
              <button
                onClick={() => setFilterRAG('AMBER')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filterRAG === 'AMBER'
                    ? 'bg-yellow-600 text-white'
                    : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                }`}
              >
                🟡 AMBER
              </button>
              <button
                onClick={() => setFilterRAG('RED')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filterRAG === 'RED'
                    ? 'bg-red-600 text-white'
                    : 'bg-red-100 text-red-700 hover:bg-red-200'
                }`}
              >
                🔴 RED
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Data Table */}
      <ExpandableDataTable
        columns={columns}
        data={paginatedProjects}
        currentPage={currentPage}
        pageSize={pageSize}
        totalElements={filteredProjects.length}
        onPageChange={setCurrentPage}
        onPageSizeChange={(newSize) => {
          setPageSize(newSize);
          setCurrentPage(0);
        }}
        onSort={setSortConfig}
        sortConfig={sortConfig}
        isLoading={isLoading}
        expandedRows={expandedRows}
        expandedContent={renderExpandedContent}
        rowClassName="transition-colors hover:bg-blue-50"
      />

      {/* Footer Summary */}
      <div className="bg-white rounded-lg shadow-md p-4 border border-gray-200">
        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-700">
            Showing <span className="font-bold text-red-600">{filteredProjects.length}</span> of{' '}
            <span className="font-bold">{projects.length}</span> projects
          </div>
          <div className="flex gap-6">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-green-500 rounded-full"></div>
              <span className="text-sm font-medium text-gray-700">GREEN:</span>
              <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-bold">
                {projects.filter(p => p.overallRAGStatus === 'GREEN').length}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-yellow-500 rounded-full"></div>
              <span className="text-sm font-medium text-gray-700">AMBER:</span>
              <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs font-bold">
                {projects.filter(p => p.overallRAGStatus === 'AMBER').length}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-red-500 rounded-full"></div>
              <span className="text-sm font-medium text-gray-700">RED:</span>
              <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-bold">
                {projects.filter(p => p.overallRAGStatus === 'RED').length}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgressList;